
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, Timestamp, limit, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { tenantProfileDocRef, setUserRecord, updateUserRecord, userDocRef, usersCollectionRef } from '../services/firestorePaths';
import { User, Role } from '../types';
import { hashPin } from '../utils/crypto';
import { defaultTenantConfig, mergeTenantConfig, TenantConfig, TenantProfileOverrides } from '../config/tenant';
import { initAnalytics } from '../services/analyticsService';
import { resolveTenantRoutingContext } from '../utils/tenantRouting';
import { generateEmployeeId } from '../utils/idGeneration';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithBadge: (badgeId: string, password: string) => Promise<void>;
  loginWithProvider: (provider: 'google' | 'microsoft' | 'saml') => Promise<void>;
  loginWithPin: (badgeId: string, pin: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    role: Role,
    regNumber?: string,
    options?: {
      roleOverride?: Role;
      statusOverride?: 'Active' | 'Pending';
      employeeIdPrefix?: string;
    }
  ) => Promise<void>;
  reauthenticate: (password: string) => Promise<boolean>;
  verifyPin: (pin: string) => Promise<boolean>;
  updatePin: (newPin: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const resolveExpectedTenantId = (): string => {
  if (typeof window === 'undefined') return defaultTenantConfig.tenantId;
  const routing = resolveTenantRoutingContext({
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    baseDomain: 'aspis.software',
  });

  return routing.tenantSlug || defaultTenantConfig.tenantId;
};

const resolveTenantConfigForAuth = async (): Promise<TenantConfig> => {
  const tenantId = resolveExpectedTenantId();
  try {
    const profile = await getDoc(tenantProfileDocRef(db, tenantId));
    const overrides = profile.exists() ? (profile.data() as TenantProfileOverrides) : null;
    return mergeTenantConfig({
      ...defaultTenantConfig,
      tenantId,
      displayName: overrides?.displayName || defaultTenantConfig.displayName,
    }, overrides);
  } catch {
    return {
      ...defaultTenantConfig,
      tenantId,
    };
  }
};

// Helper to sanitize error messages
export const getFriendlyErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    
    const code = error.code;
    
    switch (code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
            return 'Invalid email or password.';
        case 'auth/email-already-in-use':
            return 'An account with this email address already exists.';
        case 'auth/weak-password':
            return 'Password must be at least 6 characters.';
        case 'auth/too-many-requests':
            return 'Too many requests. Please wait a moment before trying again.';
        case 'auth/network-request-failed':
            return 'Unable to connect. Please check your internet connection.';
        case 'auth/user-disabled':
            return 'This account has been disabled.';
        case 'auth/popup-closed-by-user':
          return 'Sign-in window was closed before completion.';
        case 'auth/popup-blocked':
          return 'Popup was blocked by your browser. Allow popups and try again.';
        case 'auth/operation-not-allowed':
          return 'This authentication method is not enabled for the workspace.';
        case 'auth/requires-recent-login':
            return 'Please log in again to verify your identity.';
        case 'permission-denied':
            return 'Badge Login unavailable. System rules block ID lookup. Please use Email Login.';
        case 'unavailable':
            return 'Service temporarily unavailable. Please check your connection.';
    }

    // Clean up generic Firebase messages
    let message = error.message || 'An unexpected error occurred.';
    if (message.includes('Firebase:')) {
        message = message.replace('Firebase: ', '');
        message = message.replace(/\(auth\/[-\w]+\)\.?/, '').trim();
        if (message.startsWith('Error: ')) message = message.substring(7);
    }
    return message;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const attemptFoundingAdminPromotion = async (candidate: User, fbUser: FirebaseUser) => {
    const normalizedEmail = String(fbUser.email || '').trim().toLowerCase();
    if (!normalizedEmail) return false;
    if (candidate.status !== 'Pending') return false;

    try {
      const response = await fetch('/api/setup/public-tenant-context');
      if (!response.ok) return false;

      const payload = await response.json();
      const foundingAdminEmail = String(payload?.foundingAdmin?.email || '').trim().toLowerCase();
      const autoApproved = payload?.foundingAdmin?.autoApproved !== false;

      if (!foundingAdminEmail || !autoApproved || foundingAdminEmail !== normalizedEmail) {
        return false;
      }

      const patchedUser: User = {
        ...candidate,
        role: Role.Admin,
        status: 'Active',
      };

      await updateUserRecord(db, fbUser.uid, patchedUser.tenantId, {
        role: Role.Admin,
        status: 'Active',
      });

      setUser(patchedUser);
      return true;
    } catch (promotionError) {
      console.warn('Founding admin promotion check failed', promotionError);
      return false;
    }
  };

  const applyResolvedUser = async (candidate: User, fbUser: FirebaseUser) => {
    if (candidate.pinHash && !candidate.pin) {
      candidate.pin = '****';
    }

    candidate.tenantId = candidate.tenantId || defaultTenantConfig.tenantId;
    candidate.tenantName = candidate.tenantName || defaultTenantConfig.displayName;

    const promoted = await attemptFoundingAdminPromotion(candidate, fbUser);
    if (!promoted) {
      setUser(candidate);
    }

    initAnalytics({
      tenantId: candidate.tenantId || defaultTenantConfig.tenantId,
      userId: fbUser.uid,
      role: promoted ? Role.Admin : candidate.role,
      deviceType: localStorage.getItem('aspis_device_type') || 'byod',
    });
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
      setFirebaseUser(fbUser);
      
      if (fbUser) {
        setIsLoading(true);
        // Real-time listener for user profile
        const expectedTenantId = resolveExpectedTenantId();
        const currentUserRef = userDocRef(db, fbUser.uid, expectedTenantId);
        const unsubscribeSnapshot = onSnapshot(currentUserRef, (docSnap) => {
            if (docSnap.exists()) {
                const userData = docSnap.data() as User;
            if (userData.tenantId && expectedTenantId && userData.tenantId !== expectedTenantId) {
              setError('This account is assigned to a different company workspace. Use your company subdomain to sign in.');
              signOut(auth).catch(() => undefined);
              setIsLoading(false);
              return;
            }
                void applyResolvedUser(userData, fbUser);
            } else {
                // Fallback: users can still be stored in legacy path while namespaced reads are enabled.
                void (async () => {
                  try {
                    const legacySnap = await getDoc(doc(db, 'users', fbUser.uid));
                    if (legacySnap.exists()) {
                      const legacyUser = legacySnap.data() as User;
                      await applyResolvedUser(legacyUser, fbUser);
                      return;
                    }
                  } catch (legacyError) {
                    console.warn('Legacy user profile fallback failed', legacyError);
                  }

                  setUser({
                      uid: fbUser.uid,
                      email: fbUser.email || '',
                      name: fbUser.displayName || 'User',
                      tenantId: defaultTenantConfig.tenantId,
                      tenantName: defaultTenantConfig.displayName,
                      role: Role.Pending,
                      status: 'Pending',
                      compliance: []
                  });
                })();
            }
            setIsLoading(false);
        }, (err) => {
            console.error("Profile sync error", err);
            setIsLoading(false);
        });

        return () => unsubscribeSnapshot();
      } else {
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      console.error(err);
      const msg = getFriendlyErrorMessage(err);
      setError(msg);
      throw new Error(msg); 
    }
  };

  const loginWithBadge = async (badgeId: string, password: string) => {
      setError(null);
      try {
          const tenantConfig = await resolveTenantConfigForAuth();
          const normalizedBadge = String(badgeId || '').trim().toUpperCase();
          const cleanedBadge = /[A-Z]/.test(normalizedBadge)
            ? normalizedBadge
            : `${tenantConfig.badgePrefix}${normalizedBadge.replace(/\D/g, '')}`;
          const q = query(
            usersCollectionRef(db, tenantConfig.tenantId),
            where('tenantId', '==', tenantConfig.tenantId),
            where('employeeId', '==', cleanedBadge),
            limit(1)
          );
          const snapshot = await getDocs(q);
          
          if (snapshot.empty) {
              throw new Error("Invalid Employee Badge ID. Please check your ID or use Email Login.");
          }

          const userData = snapshot.docs[0].data();
          await login(userData.email, password);

      } catch (err: any) {
          console.error(err);
          if (err.code === 'permission-denied' || err.message.includes('permission-denied')) {
              const errObj = { code: 'permission-denied', message: 'Badge Login disabled by security policy.' };
              setError(getFriendlyErrorMessage(errObj));
              throw new Error("Badge Login is disabled. Please use Email Login.");
          }
          const msg = getFriendlyErrorMessage(err);
          setError(msg);
          throw new Error(msg);
      }
  };

  const loginWithProvider = async (provider: 'google' | 'microsoft' | 'saml') => {
    setError(null);
    try {
      if (provider === 'google') {
        const googleProvider = new GoogleAuthProvider();
        await signInWithPopup(auth, googleProvider);
        return;
      }

      if (provider === 'microsoft') {
        const microsoftProviderId = (import.meta.env.VITE_AUTH_MICROSOFT_PROVIDER_ID || 'microsoft.com').trim();
        const microsoftProvider = new OAuthProvider(microsoftProviderId);
        await signInWithPopup(auth, microsoftProvider);
        return;
      }

      const samlProviderId = (import.meta.env.VITE_AUTH_SAML_PROVIDER_ID || '').trim();
      if (!samlProviderId) {
        throw new Error('SAML login is not configured for this workspace.');
      }

      const samlProvider = new SAMLAuthProvider(samlProviderId);
      await signInWithPopup(auth, samlProvider);
    } catch (err: any) {
      console.error(err);
      const msg = getFriendlyErrorMessage(err);
      setError(msg);
      throw new Error(msg);
    }
  };

  const loginWithPin = async (badgeId: string, pin: string) => {
    throw new Error("Please use Badge ID + Password for initial login.");
  };

  const verifyPin = async (pin: string): Promise<boolean> => {
      if (!user) return false;
      try {
          // Fetch fresh doc to ensure PIN matches server state
          const freshUserDoc = await getDoc(userDocRef(db, user.uid, user.tenantId));
          
          if (freshUserDoc.exists()) {
            const data = freshUserDoc.data();
              if (data.pinHash) {
                  const inputHash = await hashPin(pin);
                  return inputHash === data.pinHash;
              }
              if (data.pin) {
                  return data.pin === pin;
              }
          }
          return false;
      } catch (e) {
          console.error("PIN Verification Error", e);
          return false;
      }
  };

  const updatePin = async (newPin: string) => {
      if (!user) return;
      if (!/^\d{4}$/.test(newPin)) throw new Error("PIN must be 4 digits.");
      
      try {
          const hashed = await hashPin(newPin);
          await updateUserRecord(db, user.uid, user.tenantId, {
              pinHash: hashed,
              pin: null, 
              pinLastUpdated: new Date().toISOString()
          });
      } catch (e) {
          console.error(e);
          throw new Error("Failed to update PIN");
      }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    role: Role,
    regNumber?: string,
    options?: {
      roleOverride?: Role;
      statusOverride?: 'Active' | 'Pending';
      employeeIdPrefix?: string;
    }
  ) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const fbUser = userCredential.user;
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true,
      };
      await sendEmailVerification(fbUser, actionCodeSettings);

      // Generate tenant badge ID using configured prefix + YYMM + random digits
      const tenantConfig = await resolveTenantConfigForAuth();
      const employeeId = generateEmployeeId({
        badgePrefix: options?.employeeIdPrefix || tenantConfig.badgePrefix,
        identifierPolicy: {
          ...(tenantConfig.identifierPolicy || {}),
          ...(options?.employeeIdPrefix ? { employeePrefix: options.employeeIdPrefix } : {}),
        },
      });

      const newUser: User = {
        uid: fbUser.uid,
        email,
        name,
        tenantId: tenantConfig.tenantId,
        tenantName: tenantConfig.displayName,
        role: options?.roleOverride || role,
        status: options?.statusOverride || 'Pending',
        regNumber,
        employeeId, // Auto assigned
        compliance: []
      };

  await setUserRecord(db, fbUser.uid, tenantConfig.tenantId, newUser);
      setUser(newUser);
      setFirebaseUser(fbUser);
      
    } catch (err: any) {
      console.error(err);
      const msg = getFriendlyErrorMessage(err);
      setError(msg);
      throw new Error(msg);
    }
  };

  const reauthenticate = async (password: string): Promise<boolean> => {
      if (!auth.currentUser || !auth.currentUser.email) return false;
      try {
          const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
          await reauthenticateWithCredential(auth.currentUser, credential);
          return true;
      } catch (err) {
          console.error("Reauth failed", err);
          return false;
      }
  };

  const refreshUser = async () => {
      if (auth.currentUser) {
          try {
              await auth.currentUser.reload();
              setFirebaseUser({ ...auth.currentUser } as FirebaseUser);
          } catch (err) {
              console.error("Error refreshing user", err);
          }
      }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, login, loginWithBadge, loginWithProvider, loginWithPin, verifyPin, updatePin, register, reauthenticate, logout, refreshUser, isLoading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
